/**
 * Data-source tags for the Client Care cadence rows.
 *
 * The `mycrm` tag used to be the hardcoded literal "From Loan Market" with the
 * tip "Sync via MyCRM". That was wrong twice over on a tenant with no CRM at
 * all (nothing will ever populate those cadences), and wrong even for a Loan
 * Market tenant, because the data source is the CRM (MyCRM), not the
 * aggregator. No aggregator brand is emitted from here in any state.
 */

export type DataSource = 'auto' | 'date' | 'mycrm';

/** What the page knows about the tenant's CRM when it renders the tags. */
export type CrmSourceState =
  /** At least one active `crm_connections` doc. */
  | 'connected'
  /** The status call succeeded and there is no active connection. */
  | 'disconnected'
  /** Still loading, or the status call failed. Never blocks the admin. */
  | 'unknown';

export interface CrmSourceStatus {
  state: CrmSourceState;
  /** Display name of the connected provider, e.g. 'MyCRM'. Null unless connected. */
  providerLabel: string | null;
}

export interface SourceMeta {
  label: string;
  tip: string;
  cls: string;
  /** True only when the cadence can never fire, because no CRM feeds it. */
  disabled: boolean;
}

const AUTO_CLS = 'bg-green-500/15 text-green-300 border-green-500/30';
const NEEDS_CLS = 'bg-amber-500/15 text-amber-300 border-amber-500/30';
const CRM_CLS = 'bg-blue-500/15 text-blue-300 border-blue-500/30';

/** Provider id -> display name. Anything unmapped stays generic, never a brand. */
const PROVIDER_LABELS: Record<string, string> = {
  mycrm: 'MyCRM',
  monday: 'Monday',
};

export function crmProviderLabel(provider: string | null | undefined): string | null {
  if (!provider) return null;
  return PROVIDER_LABELS[provider] ?? null;
}

/**
 * Derive the CRM state from `useCrmConnection`.
 *
 * Fail direction is deliberate: while loading, and on any error, the state is
 * 'unknown', which renders the neutral "From CRM" tag and leaves the toggle
 * enabled. A failed status call must never look like "you have no CRM", and
 * must never take a control away from an admin.
 */
export function resolveCrmSourceStatus(input: {
  connections: Array<{ provider: string; status: string }>;
  loading: boolean;
  error: string | null;
}): CrmSourceStatus {
  if (input.loading || input.error) return { state: 'unknown', providerLabel: null };
  const active = input.connections.find((c) => c.status === 'active');
  if (!active) return { state: 'disconnected', providerLabel: null };
  return { state: 'connected', providerLabel: crmProviderLabel(active.provider) };
}

/** The tag for one cadence's data source, given the tenant's CRM state. */
export function resolveSourceMeta(source: DataSource, crm: CrmSourceStatus): SourceMeta {
  if (source === 'auto') {
    return {
      label: 'Auto',
      tip: 'From your loan book, zero effort.',
      cls: AUTO_CLS,
      disabled: false,
    };
  }
  if (source === 'date') {
    return {
      label: 'Needs a date',
      tip: 'One-time entry, then recurs.',
      cls: NEEDS_CLS,
      disabled: false,
    };
  }

  if (crm.state === 'disconnected') {
    return {
      label: 'Needs CRM data',
      tip: 'Requires a connected CRM. Contact support to connect yours.',
      cls: NEEDS_CLS,
      // The row stays visible: hiding it would remove discoverability and
      // silently strip cadences an admin may already have enabled.
      disabled: true,
    };
  }

  const from = crm.state === 'connected' && crm.providerLabel ? crm.providerLabel : 'your CRM';
  return {
    label: 'From CRM',
    tip: `Synced from ${from}: DOB, contact, fixed-rate and IO dates.`,
    cls: CRM_CLS,
    disabled: false,
  };
}
